import { Injectable } from '@angular/core';
import { environment } from '@environment/environment';
import {
	DATE_YMD,
	INTEGRATION_TYPE,
	IS_NOT_EMPTY,
	IS_NOT_NULL_OR_UNDEFINED,
} from 'cargo-chief-commons';

@Injectable({
	providedIn: 'root',
})
export class UrlService {
	baseUrl = '';

	constructor() {
		this.baseUrl = environment.apiBaseUrl;
	}

	loginUrl(): string {
		return `${this.baseUrl}/login`;
	}

	loginSetupUrl(): string {
		return `${this.baseUrl}/loginSetup`;
	}

	contactsUrl(carrierId: string): string {
		return `${this.baseUrl}/getContacts/${carrierId}`;
	}

	capacityEmailsUrl(pageNumber: number, startDate: string, endDate: string) {
		return `${this.baseUrl}/capacityEmails?page=${pageNumber}&startDate=${startDate}&endDate=${endDate}`;
	}

	deleteAccountProjects(accountId: number): string {
		return `${this.baseUrl}/account/${accountId}/projects`;
	}

	adminAccountsListUrl(pageNumber: number, filter: any): string {
		if (filter.plan === null) {
			return (
				`${this.baseUrl}/accounts?page=${pageNumber}&pageSize=${filter.pageSize}&sort=${filter.sortOrder}&column=${filter.sortColumn}
			&businessName=${filter.searchTerm}&isSubscriptionColumn=${filter.isSubscriptionColumn}
			&includeBrokers=${filter.isBroker}&includeCarriers=${filter.isCarrier}&includeShippers=${filter.isShipper}&includePartners=${filter.isPartner}` +
				`&myAccounts=${filter?.myAccounts ? filter.myAccounts : false}&mappedCarriers=${
					IS_NOT_NULL_OR_UNDEFINED(filter.mappedCarriers) ? filter.mappedCarriers : ''
				}`
			);
		} else {
			return (
				`${this.baseUrl}/accounts?page=${pageNumber}&pageSize=${filter.pageSize}&sort=${filter.sortOrder}&column=${filter.sortColumn}
			&businessName=${filter.searchTerm}&isSubscriptionColumn=${filter.isSubscriptionColumn}&plan=${filter.plan}
			&includeBrokers=${filter.isBroker}&includeCarriers=${filter.isCarrier}&includeShippers=${filter.isShipper}&includePartners=${filter.isPartner}` +
				`&myAccounts=${filter?.myAccounts ? filter.myAccounts : false}&mappedCarriers=${
					IS_NOT_NULL_OR_UNDEFINED(filter.mappedCarriers) ? filter.mappedCarriers : ''
				}`
			);
		}
	}

	listParterAccountsUrl(): string {
		return `${this.baseUrl}/accounts/partners`;
	}

	getLoads(
		pageNumber: number,
		pageSize: number,
		activeOnly: boolean,
		demoLoad: boolean,
		myLoads?: boolean
	): string {
		return `${
			this.baseUrl
		}/loads?page=${pageNumber}&pageSize=${pageSize}&activeOnly=${activeOnly}&demoLoad=${demoLoad}&myLoads=${
			IS_NOT_NULL_OR_UNDEFINED(myLoads) ? myLoads : false
		}`;
	}

	getLoadsV2(
		pageNumber: number,
		pageSize: number,
		activeOnly: boolean,
		demoLoad: boolean,
		myLoads?: boolean
	): string {
		return `${
			this.baseUrl
		}/loads/v2?page=${pageNumber}&pageSize=${pageSize}&activeOnly=${activeOnly}&demoLoad=${demoLoad}&myLoads=${
			IS_NOT_NULL_OR_UNDEFINED(myLoads) ? myLoads : false
		}`;
	}

	createAccountUrl(): string {
		return `${this.baseUrl}/account`;
	}

	createCarrierUrl(): string {
		return `${this.baseUrl}/carrier`;
	}

	carrierProfileUrl(carrierId: string) {
		return `${this.baseUrl}/admin/carrier/${carrierId}`;
	}

	myCarrierPacketsUpdateProfileUrl(carrierId: string) {
		return `${this.baseUrl}/admin/myCarrierPackets/updateProfile/${carrierId}`;
	}

	bidsListUrl(pageNumber: number = 0, pageSize: number = 25): string {
		return `${this.baseUrl}/bids?pageNumber=${pageNumber}&pageSize=${pageSize}`;
	}

	quoteActivityUrl(
		quoteId: string,
		pageNumber: number = 0,
		pageSize: number = 25,
		carrierQuery: string = ''
	): string {
		return `${this.baseUrl}/quote/${quoteId}/activity?pageNumber=${pageNumber}&pageSize=${pageSize}&carrierQuery=${carrierQuery}`;
	}

	// F7e: per-load carrier email conversation (c4 GET /quote/:quoteId/emails + .../emails/:emailId/part)
	quoteEmailsUrl(quoteId: string, page: number = 0, pageSize: number = 25): string {
		return `${this.baseUrl}/quote/${encodeURIComponent(quoteId)}/emails?page=${page}&pageSize=${pageSize}`;
	}

	// CN-8638: the conversation for a whole load (across all its quotes/re-runs).
	loadEmailsUrl(loadId: string, page: number = 0, pageSize: number = 25): string {
		return `${this.baseUrl}/load/${encodeURIComponent(loadId)}/emails?page=${page}&pageSize=${pageSize}`;
	}

	quoteEmailPartUrl(quoteId: string, emailId: string): string {
		return `${this.baseUrl}/quote/${encodeURIComponent(quoteId)}/emails/${encodeURIComponent(
			emailId
		)}/part`;
	}

	quoteDetailsUrl(quoteId: string): string {
		return `${this.baseUrl}/quote/${quoteId}`;
	}

	getTrucksForReviewUrl(page: number, search: object): string {
		if (search['email']) {
			return `${this.baseUrl}/admin/getTrucksForReview?filterType=email&filterTerm=${search['email']}&index=${page}`;
		} else if (search['carrier_id']) {
			return `${this.baseUrl}/admin/getTrucksForReview?filterType=carrier_id&filterTerm=${search['carrier_id']}&index=${page}`;
		} else {
			return `${this.baseUrl}/admin/getTrucksForReview?index=${page}`;
		}
	}

	getTrucksForReviewCountUrl(search: object): string {
		if (search['email']) {
			return `${this.baseUrl}/admin/getTrucksForReviewCount?filterType=email&filterTerm=${search['email']}`;
		} else if (search['carrier_id']) {
			return `${this.baseUrl}/admin/getTrucksForReviewCount?filterType=carrier_id&filterTerm=${search['carrier_id']}`;
		} else {
			return `${this.baseUrl}/admin/getTrucksForReviewCount`;
		}
	}

	setContactInfoUrl(parsedCapacityEmailId: number): string {
		return `${this.baseUrl}/parsedCapacityEmailId/${parsedCapacityEmailId}/contact`;
	}

	trailerTypeUrl(parsedCapacityEmailId: number, trailerType: string): string {
		return `${this.baseUrl}/admin/updateTrailerType/${parsedCapacityEmailId}/${trailerType}`;
	}

	addTruckUrl(): string {
		return `${this.baseUrl}/admin/addNewParsedTruck`;
	}

	updateTruckUrl(): string {
		return `${this.baseUrl}/admin/updateParsedTruck`;
	}

	removeTruckUrl(truckId: number) {
		return `${this.baseUrl}/admin/removeTruck/${truckId}`;
	}

	createQuote(): string {
		return `${this.baseUrl}/quote`;
	}

	searchCarriersUrl(carrierName: string): string {
		return `${this.baseUrl}/searchCarriers?q=${carrierName}`;
	}

	stopActivityUrl(quoteId: string): string {
		return `${this.baseUrl}/bid/${quoteId}/stopBidding`;
	}

	cancelLoads(): string {
		return `${this.baseUrl}/bid/stopBiddingBulk`;
	}

	logoutUrl(): string {
		return `${this.baseUrl}/logout`;
	}

	topSearchesUrl(): string {
		return `${this.baseUrl}/topSearches`;
	}

	updateSubscriptionStatusUrl(subscriptionId: number): string {
		return `${this.baseUrl}/subscription/${subscriptionId}`;
	}

	updateCarrierStatus(carrierId: number, status: string): string {
		return `${this.baseUrl}/admin/carrier/updateStatus/${carrierId}/${status}`;
	}

	analyzeTrucksUrl(): string {
		return `${this.baseUrl}/admin/trucks/search`;
	}

	getBidDetailsUrl(bidId: string): string {
		return `${this.baseUrl}/bid/${bidId}`;
	}

	quoteReviewUrl(quoteId: string): string {
		return `${this.baseUrl}/quote/${quoteId}/review`;
	}

	acceptBidUrl(bidId: string): string {
		return `${this.baseUrl}/bid/${bidId}/accept`;
	}

	counterBidUrl(bidId: string, isBrokerCounter: boolean): string {
		return `${this.baseUrl}/bid/${bidId}/counter/${isBrokerCounter}`;
	}

	// T8: clear a per-carrier takeover so the auto-negotiator resumes for this bid.
	resumeAutoNegotiationUrl(bidId: string): string {
		return `${this.baseUrl}/bid/${bidId}/resume-auto-negotiation`;
	}

	bulkCounterBidUrl(): string {
		return `${this.baseUrl}/bids/bulkCounter`;
	}

	rejectBidUrl(): string {
		return `${this.baseUrl}/bid/reject`;
	}

	bidContactedUrl(): string {
		return `${this.baseUrl}/bid/contacted`;
	}

	postBidUrl(bidId: string): string {
		return `${this.baseUrl}/bid/${bidId}`;
	}

	rateConfirmationHtmlUrl(bookingId): string {
		return `${this.baseUrl}/rateConfirmation/${bookingId}/html`;
	}

	rateConfirmationSignUrl(bookingId): string {
		return `${this.baseUrl}/rateConfirmation/${bookingId}/sign`;
	}

	rateConfirmationCreateUrl(): string {
		return `${this.baseUrl}/rateConfirmation`;
	}

	reviewEmailCompletedUrl(parsedEmailId: number, carrierId: string): string {
		return `${this.baseUrl}/admin/review_completed/${parsedEmailId}/${carrierId}`;
	}

	reviewEmailExcludedUrl(parsedEmailId: number): string {
		return `${this.baseUrl}/admin/markExcluded/${parsedEmailId}`;
	}

	bookinDetailsUrl(bookingId): string {
		return `${this.baseUrl}/rateConfirmation/${bookingId}`;
	}

	rateConfirmationPDFUrl(bookingId): string {
		return `${this.baseUrl}/rateConfirmation/${bookingId}/pdf`;
	}

	defaultTrailerTypeUrl(carrierId): string {
		return `${this.baseUrl}/carrier/${carrierId}`;
	}

	updatePrimaryContact(isPrimaryContact, contactId, carrierId): string {
		return `${this.baseUrl}/admin/carrier/${carrierId}/updateIsPrimaryContact/${contactId}/${isPrimaryContact}`;
	}

	createPrimaryContact(emailAddress, carrierId): string {
		return `${this.baseUrl}/carrier/${carrierId}/primaryContact/${emailAddress}`;
	}

	setPrimaryContactAndUnsetOtherEmails(carrierContactMapId): string {
		return `${this.baseUrl}/carrier/contact/${carrierContactMapId}/primary`;
	}

	setCarrierAccountContact(): string {
		return `${this.baseUrl}/carrier/account/contact`;
	}

	getJobs(userId: number): string {
		return `${this.baseUrl}/jobs/user/${userId}`;
	}

	getNotifications(userId: number): string {
		return `${this.baseUrl}/notifications/user/${userId}`;
	}

	updateNotifications(): string {
		return `${this.baseUrl}/notifications`;
	}

	deleteNotifications(): string {
		return `${this.baseUrl}/notifications`;
	}

	getImportStats(userId: number, page: number, pageSize: number): string {
		return `${this.baseUrl}/importStats/${userId}?page=${page}&pageSize=${pageSize}`;
	}

	updateIsMailReciever(isMailReciever, contactId, carrierId): string {
		return `${this.baseUrl}/admin/carrier/${carrierId}/updateIsMailReciever/${contactId}/${isMailReciever}`;
	}

	contactCreateUrl(carrierId: string): string {
		return `${this.baseUrl}/carrier/${carrierId}/contact`;
	}

	accountDetailsUrl(accountId): string {
		return `${this.baseUrl}/accountDetails?accountId=${accountId}`;
	}

	getActiveUsersByAccountIdUrl(accountId, page: number = 0, pageSize: number = 25): string {
		return `${this.baseUrl}/getActiveUsersByAccountId?accountId=${accountId}&page=${page}&pageSize=${pageSize}`;
	}

	capacityMapDataUrl(): string {
		return `${this.baseUrl}/capacityMapData`;
	}

	registerDeviceUrl(): string {
		return `${this.baseUrl}/registerDevice`;
	}

	updateDeviceUrl(): string {
		return `${this.baseUrl}/updateDevice`;
	}

	bulkLoadsMatchList(pageNumber: number): string {
		return `${this.baseUrl}/broker/bulkLoadsMatchList?${pageNumber}`;
	}

	bulkMatchLoads(s3Key: string): string {
		return `${this.baseUrl}/bulkMatchLoads/${s3Key}`;
	}

	getTruckLaneSearchUrl(): string {
		return `${this.baseUrl}/truck/lane/search`;
	}

	getCapacityUrl(): string {
		return `${this.baseUrl}/capacity`;
	}

	getCarriersByCustomerUrl(customerId): string {
		return `${this.baseUrl}/carriers/customer/${customerId}`;
	}

	updateAccountSettingUrl(
		accountSettingId: number,
		value: any,
		accountId: number,
		settingName: string
	): string {
		return `${this.baseUrl}/accountSetting?${
			accountSettingId != null
				? 'id=' + accountSettingId + 'value=' + value
				: 'accountId=' + accountId + '&value=' + value + '&settingName=' + settingName
		}`;
	}

	updateAccountCapacitySearchSettingsUrl(): string {
		return `${this.baseUrl}/accountSetting/capacitySearch/`;
	}

	getExcludedCarrierListForAccountUrl(
		pageNumber: number,
		pageSize: number,
		search: any,
		onlyExcluded: boolean
	): string {
		return `${this.baseUrl}/account/excludedCarrierList?page=${pageNumber}&pageSize=${pageSize}
			&searchTerm=${search.searchTerm}&searchField=${search.searchField}&onlyExcluded=${onlyExcluded}`;
	}

	excludeCarrierUrl(
		carrierId: string,
		isExclude: string,
		keepInWorkflows: boolean = false,
		reason: string = null
	): string {
		let url = `${this.baseUrl}/account/includeOrExcludeCarrier?carrierId=${carrierId}&includeOrExclude=${isExclude}&keepInWorkflows=${keepInWorkflows}`;
		if (reason != null && reason.length > 0) {
			url += `&reason=${encodeURIComponent(reason)}`;
		}
		return url;
	}

	restoreProjectCarrierMapUrl(): string {
		return `${this.baseUrl}/projectCarrierMap/restore`;
	}

	getCarrierListAdminUrl(pageNumber: number, search: any, size: number = 25): string {
		return (
			`${this.baseUrl}/admin/carriers?page=${pageNumber}&pageSize=${size}&searchTerm=
		${search.searchField === 'location' ? search.locationData : search.searchTerm}&searchField=${
			search.searchField
		}
		&sortColumn=${search.sortColumn || ''}&sortOrder=${search.sortOrder || 'desc'}&inNetwork=${
			search.inNetwork
		}` +
			`&byAccount=${search.byAccount}&authorized=${search.authorized}&withoutLanes=${search.withoutLanes}&accountCarrierMap=${search.accountCarrierMap}` +
			`&notOnboarded=${search.notOnboarded ? search.notOnboarded : false}&carrierRecruiter=${
				search.carrierRecruiter ? search.carrierRecruiter : false
			}` +
			`&accountIds=${search.accountIds ? search.accountIds : ''}&stageIds=${
				search.stageIds ? search.stageIds : ''
			}` +
			`&nextContactDate=${search.nextContactDate ? DATE_YMD(search.nextContactDate) : ''}`
		);
	}

	adminLanesList(pageNumber: number, filter: any): string {
		return `${this.baseUrl}/admin/truckLaneReport?pageSize=${filter.pageSize}&page=${pageNumber}&originCity=${filter.originCity}
			&originState=${filter.originState}
			&destCity=${filter.destinationCity}&destState=${filter.destinationState}&trailerType=${filter.trailerType}
      &destinationStates=${filter.destinationStates}&needsReview=${filter.needsReview}`;
	}

	adminSearchTruckDetails(pageNumber: number, filter: any): string {
		return `${this.baseUrl}/getSearchTrucksList?page=${pageNumber}&businessName=${filter.businessName}`;
	}

	adminLaneSearch(): string {
		return `${this.baseUrl}/admin/truckLanes/search`;
	}

	adminOAuthClients(pageNumber: number, pageSize: number): string {
		return `${this.baseUrl}/admin/oauthClients?page=${pageNumber}&pageSize=${pageSize}`;
	}

	adminOAuthClientProfile(clientId: number): string {
		return `${this.baseUrl}/admin/oauthClient?id=${clientId}`;
	}

	getAccountSetting(accountId: number): string {
		return `${this.baseUrl}/accountSetting/${accountId}`;
	}

	adminResetPassword(): string {
		return `${this.baseUrl}/admin/resetUserPassword`;
	}

	adminDeactivateUser(): string {
		return `${this.baseUrl}/admin/deactivateUser`;
	}

	adminActivateUser(): string {
		return `${this.baseUrl}/admin/activateUser`;
	}

	adminTruckLanesCarrierList(pageNumber: number, pageSize: number): string {
		return `${this.baseUrl}/admin/truckLanes/carriersList?pageSize=${pageSize}&page=${pageNumber}`;
	}

	adminUpdateAccountDetails(accountId: number): string {
		return `${this.baseUrl}/admin/account/${accountId}`;
	}

	accountSettingBulkUpdate(): string {
		return `${this.baseUrl}/accountSetting/bulkUpdate`;
	}

	getAPIToken(accountId: any): string {
		return `${this.baseUrl}/getApiTokenByAccountId?accountId=${accountId}`;
	}

	generateToken(accountId: any, roles: string): string {
		return `${this.baseUrl}/token/${accountId}?role=${roles}`;
	}

	removeToken(accountId: any): string {
		return `${this.baseUrl}/removeApiToken/${accountId}`;
	}

	tokenEvict(accountId: any): string {
		return `${this.baseUrl}/token/evict/${accountId}`;
	}

	// The token is sent in the request body, not the path. c4 logs every request URI at INFO
	// (C4RequestLoggingFilter), so a credential in the path is a credential in the logs.
	setApiToken(accountId: number): string {
		return `${this.baseUrl}/setApiToken/${accountId}`;
	}

	setFromEmail(accountId: number, email: string): string {
		return `${this.baseUrl}/setFromEmail/${accountId}/${email}`;
	}

	signedUrl(bucketName: string, fileName: string = ''): string {
		return `${this.baseUrl}/preSignedUrl?bucketName=${bucketName}&fileName=${fileName}`;
	}

	addCarriersFromS3CsvFile(key, accountId: any, userId: any, isExcluded: boolean): string {
		return `${this.baseUrl}/addCarriersFromS3CsvFile/${key}/${accountId}/byUser/${userId}/${isExcluded}`;
	}

	getQuotes(): string {
		return `${this.baseUrl}/quotes`;
	}

	getQuotesV2(pageNumber: number, pageSize: number): string {
		return `${this.baseUrl}/quotes/v2?pageNumber=${pageNumber}&pageSize=${pageSize}`;
	}

	adminTruckLanesSearch(page: number, pageSize: number, businessName: string): string {
		return `${this.baseUrl}/truckLanesSearch?page=${page}&pageSize=${pageSize}&businessName=${businessName}`;
	}

	adminTruckLanesSearchDetails(id: string): string {
		return `${this.baseUrl}/truckLanesSearch/${id}`;
	}

	getRegionMap(): string {
		return `${this.baseUrl}/regionMap`;
	}

	createLane(): string {
		return `${this.baseUrl}/lane`;
	}

	addNewTruckLane(): string {
		return `${this.baseUrl}/admin/createTruckLane`;
	}

	removeLane(): string {
		return `${this.baseUrl}/admin/deleteTruckLane`;
	}

	updateLane(): string {
		return `${this.baseUrl}/admin/updateTruckLane`;
	}

	refreshLane(): string {
		return `${this.baseUrl}/admin/confirmTruckLane`;
	}

	uploadLanesCSV(): string {
		return `${this.baseUrl}/admin/uploadLanes`;
	}

	createAccount(): string {
		return `${this.baseUrl}/account`;
	}

	adminCreateAccount(): string {
		return `${this.baseUrl}/admin/account`;
	}

	adminAddUser(
		{ removeCarrierContact }: { removeCarrierContact: boolean } = { removeCarrierContact: false }
	): string {
		return `${this.baseUrl}/admin/addUser?removeCarrierContact=${removeCarrierContact}`;
	}

	getUser(userId: number): string {
		return `${this.baseUrl}/user/${userId}`;
	}

	updateUser(): string {
		return `${this.baseUrl}/updateUser`;
	}

	getCarrierForBid(quoteId: string, carrierData: any): string {
		return `${this.baseUrl}/bid/${quoteId}/carrierLookup?mc=${carrierData.mc || ''}&dot=${
			carrierData.dot || ''
		}&carrierName=${carrierData.carrierName || ''}`;
	}

	createBidForQuote(quoteId: string): string {
		return `${this.baseUrl}/bid/${quoteId}`;
	}

	createAndRespondToBidUrl(quoteId: string): string {
		return `${this.baseUrl}/carriers/bids/quote/${quoteId}`;
	}

	getBidByQuoteIdandCarrierId(quoteId: string, carrierId, carrierHubId: string): string {
		return `${this.baseUrl}/bid/${quoteId}/carrier?carrierHubId=${carrierHubId}${
			carrierId != undefined && carrierId != null ? '&carrierId=' + carrierId : ''
		}`;
	}

	createCarrierForBid(quoteId: string): string {
		return `${this.baseUrl}/bid/${quoteId}/addCarrier`;
	}

	moveCarrierContact(): string {
		return `${this.baseUrl}/admin/carrier/moveCarrierContact`;
	}

	authorizeCarrierContact(id): string {
		return `${this.baseUrl}/admin/carrier/${id}/authorize`;
	}

	registerUser(): string {
		return `${this.baseUrl}/signUp`;
	}

	getSearchTruckResults(id: string): string {
		return `${this.baseUrl}/getSearchTruckResults?truckSearchId=${id}`;
	}

	addOauthClient(): string {
		return `${this.baseUrl}/admin/oauthClient/add`;
	}

	updateOauthClient(): string {
		return `${this.baseUrl}/admin/oauthClient/update`;
	}

	deleteOauthClient(): string {
		return `${this.baseUrl}/admin/oauthClient/delete`;
	}

	trucksSearchForQuote(): string {
		return `${this.baseUrl}/trucks/searchForQuote`;
	}

	getTimezone(latLng: any): string {
		var timeStamp = Math.round(new Date().getTime() / 1000).toString();
		return `https://maps.googleapis.com/maps/api/timezone/json?location=${latLng}&timestamp=${timeStamp}&key=${environment.GOOGLE_API_KEY}`;
	}

	getLanesList(): string {
		return `${this.baseUrl}/lanes`;
	}

	getBookingConfirmation(): string {
		return `${this.baseUrl}/bookingConfirmation`;
	}

	createAccountMap(): string {
		return `${this.baseUrl}/accountCarrierMaps`;
	}

	deleteAccountMap(carrierId: string, accountId: number): string {
		return `${this.baseUrl}/accountCarrierMaps/${carrierId}/${accountId}`;
	}

	updateCarrier(): string {
		return `${this.baseUrl}/admin/updateCarrier`;
	}

	syncFMCSAData(): string {
		return `${this.baseUrl}/sync/fmcsa/data`;
	}

	linkLoginUrl(): string {
		return `${this.baseUrl}/linkLogin`;
	}

	updatePassword(): string {
		return `${this.baseUrl}/updatePassword`;
	}

	forgotPassword(): string {
		return `${this.baseUrl}/resetPassword`;
	}

	parsedCapacityEmailUpdateCarrierId(parsedEmailId: any, carrierId: any): string {
		return `${this.baseUrl}/parsedCapacityEmail/${parsedEmailId}/updateCarrierId/${carrierId}`;
	}

	getPlanList(activePlans: boolean = false): string {
		return `${this.baseUrl}/admin/plans?activePlans=${activePlans}`;
	}

	addCarrierFeedback(): string {
		return `${this.baseUrl}/carrier/feedback`;
	}

	updateQuoteUrl(quoteId: string): string {
		return `${this.baseUrl}/quote/${quoteId}`;
	}

	getCarrierFeedback(carrierId: string, { pageSize }): string {
		return `${this.baseUrl}/carrier/${carrierId}/feedback?pageSize=${pageSize}`;
	}

	getChangeOwnerUrl(accountId: number, userId: number) {
		return `${this.baseUrl}/admin/changeOwner/account/${accountId}/user/${userId}`;
	}

	loginFromEmailVerificationLink(userRandomString: string): string {
		return `${this.baseUrl}/email_verified/${userRandomString}`;
	}

	accountCarrierMapCountUrl(accountId: number): string {
		return `${this.baseUrl}/accountCarrierMaps/count/${accountId}`;
	}

	changePlanUrl(): string {
		return `${this.baseUrl}/user/plan/change`;
	}

	getTruckLaneSearchResults(): string {
		return `${this.baseUrl}/truck/lane/search/results`;
	}

	getBidByTruckLaneId(): string {
		return `${this.baseUrl}/bid/truck/lane/id`;
	}

	getPriceIndexes(pageNumber, pageSize, indexDate): string {
		return `${this.baseUrl}/priceIndexes?page=${pageNumber}&pageSize=${pageSize}&indexDate=${indexDate}`;
	}

	addPriceIndexes(): string {
		return `${this.baseUrl}/priceIndex`;
	}

	getFuelPrices(pageNumber, size): string {
		return `${this.baseUrl}/fuelPrices?page=${pageNumber}&pageSize=${size}`;
	}

	deleteFuelPrices(startOfWeek): string {
		return `${this.baseUrl}/fuelPrice/${startOfWeek}`;
	}

	addFuelPrices(): string {
		return `${this.baseUrl}/fuelPrice`;
	}

	updateFuelPrices(): string {
		return `${this.baseUrl}/fuelPrice`;
	}

	registerForPricingPreview(userId: number): string {
		return `${this.baseUrl}/pricingPreview/${userId}`;
	}

	addCarrierNotes(): string {
		return `${this.baseUrl}/carrierNotes`;
	}

	listCarrierNotes(carrierId: string, page: number, pageSize: number, projectId: string): string {
		return `${this.baseUrl}/carrierNotes?${
			carrierId != null ? 'carrierId=' + carrierId : ''
		}&page=${page}&pageSize=${pageSize}${projectId != null ? '&projectId=' + projectId : ''}`;
	}

	listCarrierNotesByPost(page: number, pageSize: number) {
		return `${this.baseUrl}/carrierNotes/list?page=${page}&pageSize=${pageSize}`;
	}

	getProducts() {
		return `${this.baseUrl}/products`;
	}

	addPlan() {
		return `${this.baseUrl}/plans`;
	}

	getPlanDetail(planId) {
		return `${this.baseUrl}/plans/${planId}`;
	}

	getPlanProduct(planId) {
		return `${this.baseUrl}/plans/${planId}/products`;
	}

	updatePlanProduct(planId) {
		return `${this.baseUrl}/plans/${planId}/products`;
	}

	getRecentTransactions(pageNumber: number, pageSize: number): string {
		return `${this.baseUrl}/recentTransactions?page=${pageNumber}&pageSize=${pageSize}`;
	}

	getLanePricing(): string {
		return `${this.baseUrl}/lanePricing`;
	}

	updateNote(noteId: string): string {
		return `${this.baseUrl}/carrierNotes/${noteId}`;
	}

	getPricingAdjustments() {
		return `${this.baseUrl}/priceAdjustments`;
	}

	changePricingAdjustments() {
		return `${this.baseUrl}/priceAdjustment`;
	}

	addProject(): string {
		return `${this.baseUrl}/projects`;
	}

	getProjectsList(): string {
		return `${this.baseUrl}/projects/list`;
	}

	addProjectCarrierMap(projectId: string): string {
		return `${this.baseUrl}/projectCarrierMap/${projectId}`;
	}

	deleteProject(): string {
		return `${this.baseUrl}/projects`;
	}

	getProjectById(projectId: string): string {
		return `${this.baseUrl}/projects/${projectId}`;
	}

	getProjectCarrierMap(projectId: any, page: number, pageSize: number): string {
		return `${this.baseUrl}/projectCarrierMap?projectId=${projectId}&page=${page}&pageSize=${pageSize}`;
	}

	deleteProjectCarrierMap(): string {
		return `${this.baseUrl}/projectCarrierMap`;
	}

	addPriceReview(): string {
		return `${this.baseUrl}/lanePricingReview`;
	}

	carriersLanesCount(): string {
		return `${this.baseUrl}/carriers/lanes/count`;
	}

	carriersRecentFeedbackDate(): string {
		return `${this.baseUrl}/carriers/feedback/created`;
	}

	addCarrierToProjects(carrierId: string): string {
		return `${this.baseUrl}/projectCarrierMap/carrier/${carrierId}`;
	}

	getCarrierNotesCount() {
		return `${this.baseUrl}/notesCount`;
	}

	getProjectsByCarrierIdsAndAccountId(): string {
		return `${this.baseUrl}/projects/carrierIds`;
	}

	deleteProjectCarrierMapByProjectIdAndCarrierId(projectId: any, carrierId: any): string {
		return `${this.baseUrl}/projectCarrierMap/${projectId}/${carrierId}`;
	}

	searchCarrierHQ(page: number, pageSize: number): string {
		return `${this.baseUrl}/carrier/hq?page=${page}&pageSize=${pageSize}`;
	}

	getDistanceInMiles(): string {
		return `${this.baseUrl}/distanceInMiles`;
	}

	completeRegistration(): string {
		return `${this.baseUrl}/completeRegistration`;
	}

	users(): string {
		return `${this.baseUrl}/users`;
	}

	registrationPayment(isUserRegistration: boolean = false) {
		return `${this.baseUrl}/registrationPayment?isUserRegistration=${isUserRegistration}`;
	}

	getTotalLanesCountForCarriers(accountId: number) {
		return `${this.baseUrl}/accounts/${accountId}/carriers/truckLanes/totalCount`;
	}

	getTotalCarriersCount() {
		return `${this.baseUrl}/carriers/totalCount`;
	}

	getTotalLanesCount() {
		return `${this.baseUrl}/totalLanesCount`;
	}

	searchCarriers(
		page: number,
		pageSize: number,
		{
			withContacts,
			withPrimaryContact,
			assignedToMe,
			excludeAccountCarrierMaps,
			excludeInfractionDetails,
		}: {
			withContacts?: boolean;
			withPrimaryContact?: boolean;
			assignedToMe?: boolean;
			excludeAccountCarrierMaps?: boolean;
			excludeInfractionDetails?: boolean;
		} = {
			withContacts: false,
			withPrimaryContact: false,
			assignedToMe: false,
			excludeAccountCarrierMaps: false,
			excludeInfractionDetails: false,
		}
	): string {
		return (
			`${this.baseUrl}/searchCarriers?page=${page}&pageSize=${pageSize}&withContacts=${
				withContacts || false
			}` +
			`&withPrimaryContact=${withPrimaryContact || false}&assignedToMe=${
				assignedToMe ? assignedToMe : false
			}` +
			`&excludeAccountCarrierMaps=${excludeAccountCarrierMaps || false}` +
			`&excludeInfractionDetails=${excludeInfractionDetails || false}`
		);
	}

	getLoad(loadId: string): string {
		return `${this.baseUrl}/load/${loadId}`;
	}

	inviteUser(): string {
		return `${this.baseUrl}/inviteUsers`;
	}

	updateCarrierAuthorization() {
		return `${this.baseUrl}/admin/carriers/updateAuthorization`;
	}

	updateDefaultPaymentCard(): string {
		return `${this.baseUrl}/updateDefaultPaymentCard`;
	}

	// Only used for Ascend customer signup
	ascendSignUp(): string {
		return `${this.baseUrl}/partnerSignUp`;
	}

	getDefaultPaymentCard(): string {
		return `${this.baseUrl}/defaultPaymentCard`;
	}

	getBackgroundJobs() {
		return `${this.baseUrl}/admin/backgroundJobs`;
	}

	listCarriers(page: number, pageSize: number) {
		return `${this.baseUrl}/carriers/list?page=${page}&pageSize=${pageSize}`;
	}

	getUserByEmail(email: string): string {
		return `${this.baseUrl}/userDetails/${email}`;
	}

	projectRateRequest(projectId: string): string {
		// CN-8778: POST now — annual volume + the optional scoped projectCarrierMapIds ride in the
		// body (a GET query string can't carry a large id list).
		return `${this.baseUrl}/project_rate_request/${projectId}`;
	}

	getProjectCarrierMapById(projectCarrierMapId: string): string {
		return `${this.baseUrl}/projectCarrierMap/${projectCarrierMapId}`;
	}

	submitProjectRate(
		{
			sendNotification,
			requesterUserId,
		}: {
			sendNotification: boolean;
			requesterUserId?: number;
		},
		carrierAccount: boolean = false,
		carrierUserId: number = null
	): string {
		return (
			`${this.baseUrl}/project_carrier_map?sendNotification=${sendNotification}` +
			(requesterUserId ? `&rateRequesterUserId=${requesterUserId}` : '') +
			`&carrierAccount=${carrierAccount}&carrierUserId=${carrierUserId ? carrierUserId : ''}`
		);
	}

	getProjectUserById(userId: number): string {
		return `${this.baseUrl}/project/user/${userId}`;
	}

	getCarriersByIds(): string {
		return `${this.baseUrl}/carriers/-/ids`;
	}

	getCarrierIdsByProjectIds(): string {
		return `${this.baseUrl}/projects/-/carrierIds`;
	}


	getUsersForAccount(accountId: number) {
		return `${this.baseUrl}/accounts/${accountId}/users`;
	}

	deleteContact(contactId: string): string {
		return `${this.baseUrl}/contacts/${contactId}`;
	}

	getNextSurvey(userId: any): string {
		return `${this.baseUrl}/survey/url/${userId}`;
	}

	userCompletedSurvey(userId: any, surveyId: any): string {
		return `${this.baseUrl}/survey/${userId}/${surveyId}`;
	}

	validatePlan(planName: string): string {
		return `${this.baseUrl}/plan/${planName}`;
	}

	updateLandingPage(landingPage: string, accountId: any): string {
		return `${this.baseUrl}/landing_page/${landingPage}/${accountId}`;
	}

	updateAddToStripe(addToStripe: boolean, userId: any): string {
		return `${this.baseUrl}/user/addToStripe/${addToStripe}/${userId}`;
	}

	bulkProject(s3Key: string): string {
		return `${this.baseUrl}/bulkProject/${s3Key}`;
	}

	equipmentForCarrier(carrierId: string): string {
		return `${this.baseUrl}/carrier/${carrierId}/equipment`;
	}

	addEquipmentForCarrier(carrierId: string, equipmentId: string): string {
		return `${this.baseUrl}/carrier/${carrierId}/equipment/${equipmentId}`;
	}

	removeEquipmentForCarrier(carrierId: string, equipmentId: string): string {
		return `${this.baseUrl}/carrier/${carrierId}/equipment/${equipmentId}`;
	}

	equipmentInDB(): string {
		return `${this.baseUrl}/equipment`;
	}

	recentlyViewedCarriers(page: number = 0, pageSize: number = 6): string {
		return `${this.baseUrl}/recently_viewed_carriers?page=${page}&pageSize=${pageSize}`;
	}

	getTasksForAccount(page: number = 0, pageSize: number = 25) {
		return `${this.baseUrl}/tasks/list?page=${page}&pageSize=${pageSize}`;
	}

	bulkUpdateTasks() {
		return `${this.baseUrl}/tasks`;
	}

	deleteTasks(): string {
		return `${this.baseUrl}/tasks`;
	}

	addTasks(): string {
		return `${this.baseUrl}/tasks`;
	}

	listTaskByIds(): string {
		return `${this.baseUrl}/tasks/ids`;
	}

	userCarrierMap(): string {
		return `${this.baseUrl}/user_carrier_map`;
	}

	getCustomers(
		page: number,
		pageSize: number,
		name: string,
		accountId: number,
		isBroker: boolean = false,
		projectName: string = '',
		assigneeIds: string = '',
		sortField: string = 'name',
		sortOrder: string = 'asc'
	): string {
		return `${this.baseUrl}/customers?&page=${page}&pageSize=${pageSize}&name=${name}&accountId=${accountId}&isBroker=${isBroker}&projectName=${projectName}&assigneeIds=${assigneeIds}&sortField=${sortField}&sortOrder=${sortOrder}`;
	}

	getProjectsDesiredPrice(): string {
		return `${this.baseUrl}/projects/-/desiredPrice`;
	}

	bulkUpdateProjects(): string {
		return `${this.baseUrl}/projects/batch-update`;
	}

	updatePricingOption(isEnabled: boolean): string {
		return `${this.baseUrl}/pricing_option/${isEnabled}`;
	}

	getFMCSAData(mc: string, dot: string): string {
		return `${this.baseUrl}/fmcsa?mc=${mc}&dot=${dot}`;
	}

	updatePlanUrl(): string {
		return `${this.baseUrl}/plans`;
	}

	updateCompletedRegistration(accountId: number, isCompleted = true): string {
		return `${this.baseUrl}/updateCompletedRegistration?accountId=${accountId}&isCompleted=${isCompleted}`;
	}

	pricingFeedbacks(accountId: number, page: number = 0, pageSize: number = 25): string {
		return `${this.baseUrl}/pricingFeedbacks?accountId=${accountId}&page=${page}&pageSize=${pageSize}`;
	}

	deleteCarrier(carrierId: string): string {
		return `${this.baseUrl}/carriers/${carrierId}`;
	}

	getScorecardLanes(page: number = 0, pageSize: number = 25): string {
		return `${this.baseUrl}/scorecards/lanes?&page=${page}&pageSize=${pageSize}`;
	}

	getScorecardLanesStats(): string {
		return `${this.baseUrl}/scorecards/lanes/stats`;
	}

	getCustomersScorecardLanes(page: number = 0, pageSize: number = 25): string {
		return `${this.baseUrl}/scorecards/customers?&page=${page}&pageSize=${pageSize}`;
	}

	getCustomersScorecardStats(): string {
		return `${this.baseUrl}/scorecards/customers/stats`;
	}

	getCustomersByIds(): string {
		return `${this.baseUrl}/customers/ids`;
	}

	getCarriersScorecards(page: number = 0, pageSize: number = 25): string {
		return `${this.baseUrl}/scorecards/carriers?&page=${page}&pageSize=${pageSize}`;
	}

	getCarriersScorecardStats(): string {
		return `${this.baseUrl}/scorecards/carriers/stats`;
	}

	carrierReps(
		page: number = 0,
		pageSize: number = 25,
		sortField: string = 'firstName',
		sortOrder: string = 'asc'
	): string {
		return `${this.baseUrl}/carrierReps?&page=${page}&pageSize=${pageSize}&sortField=${sortField}&sortOrder=${sortOrder}`;
	}

	historicalLoads(
		page: number = 0,
		pageSize: number = 25,
		sortField: string = 'created',
		sortOrder = 'desc'
	): string {
		return `${this.baseUrl}/historicalLoads?&page=${page}&pageSize=${pageSize}&sortField=${sortField}&sortOrder=${sortOrder}`;
	}

	historicalLoadsV2(page: number = 0, pageSize: number = 25): string {
		return `${this.baseUrl}/historicalLoads/v2?&page=${page}&pageSize=${pageSize}`;
	}

	historicalLoadsCount(): string {
		return `${this.baseUrl}/historicalLoads/count`;
	}

	accountExecs(
		page: number = 0,
		pageSize: number = 25,
		sortField: string = 'firstName',
		sortOrder: string = 'asc'
	): string {
		return `${this.baseUrl}/accountExecs?&page=${page}&pageSize=${pageSize}&sortField=${sortField}&sortOrder=${sortOrder}`;
	}

	getAllAccountUsers(accountId, page: number = 0, pageSize: number = 25): string {
		return `${this.baseUrl}/admin/account/users?accountId=${accountId}&page=${page}&pageSize=${pageSize}`;
	}

	getLoadsLocations(page: number = 0, pageSize: number = 5): string {
		return `${this.baseUrl}/scorecards/loads/locations?&page=${page}&pageSize=${pageSize}`;
	}

	getLoadsCarrierReps(page: number = 0, pageSize: number = 25): string {
		return `${this.baseUrl}/scorecards/loads/carrierReps?&page=${page}&pageSize=${pageSize}`;
	}

	getLoadsAccountExecs(page: number = 0, pageSize: number = 25): string {
		return `${this.baseUrl}/scorecards/loads/accountExecs?&page=${page}&pageSize=${pageSize}`;
	}

	getLoadsCustomers(page: number = 0, pageSize: number = 25): string {
		return `${this.baseUrl}/scorecards/loads/customers?&page=${page}&pageSize=${pageSize}`;
	}

	getLoadsCarriers(page: number = 0, pageSize: number = 25): string {
		return `${this.baseUrl}/scorecards/loads/carriers?&page=${page}&pageSize=${pageSize}`;
	}

	updateMercuryLoad(loadId: string) {
		return `${this.baseUrl}/mercuryLoads/${loadId}`;
	}

	getAccountCarrierLanes(page: number = 0, pageSize: number = 25): string {
		return `${this.baseUrl}/accountCarrierLanes?&page=${page}&pageSize=${pageSize}`;
	}

	getCarrierLanes(page: number = 0, pageSize: number = 25): string {
		return `${this.baseUrl}/carrierLanes/list?&page=${page}&pageSize=${pageSize}`;
	}

	integrationsUrl(): string {
		return `${this.baseUrl}/integrations`;
	}

	listIntegrationsUrl(integrationType: INTEGRATION_TYPE = null): string {
		return `${this.baseUrl}/integrations?integrationType=${integrationType}`;
	}

	getMyCarrierPacketsTokenUrl(): string {
		return `${this.baseUrl}/myCarrierPackets/getToken`;
	}

	clearMyCarrierPacketsTokenUrl(): string {
		return `${this.baseUrl}/myCarrierPackets/clearToken`;
	}

	clearMyCarrierPacketsLinkUrl(): string {
		return `${this.baseUrl}/myCarrierPackets/clearLink`;
	}

	onboardCarriersURL(
		integrationId: string,
		dot: string,
		carrierId: string,
		truckLaneSearchId: string = null
	): string {
		return `${this.baseUrl}/vetting/onboard?integrationId=${integrationId ? integrationId : ''}&dot=${
			dot ? dot : ''
		}&carrierId=${carrierId ? carrierId : ''}${
			IS_NOT_EMPTY(truckLaneSearchId) ? '&truckLaneSearchId=' + truckLaneSearchId : ''
		}`;
	}

	getMcleodAuthenticationUrl(): string {
		return `${this.baseUrl}/mcleod/authentication`;
	}

	getScorecardProjectMap(): string {
		return `${this.baseUrl}/scorecardProjectMap`;
	}

	geocode({ city, state, zip }: { city: string; state: string; zip?: string }): string {
		return `${this.baseUrl}/geocode?city=${city}&state=${state}&zip=${zip || ''}`;
	}

	bulkUpdateLoads(): string {
		return `${this.baseUrl}/mercuryLoads`;
	}

	getSurveys(page: number = 0, pageSize: number = 25): string {
		return `${this.baseUrl}/admin/surveys?&page=${page}&pageSize=${pageSize}`;
	}

	surveyUrl(): string {
		return `${this.baseUrl}/admin/surveys`;
	}

	deleteSurey(id: number): string {
		return `${this.baseUrl}/admin/survey/${id}`;
	}

	getUsersSurey(surveyId: number): string {
		return `${this.baseUrl}/admin/survey/${surveyId}/users`;
	}

	getPlanSettings(planId: number): string {
		return `${this.baseUrl}/planSettings?planId=${planId}`;
	}

	updatePlanSettings(planId: number): string {
		return `${this.baseUrl}/planSettings?planId=${planId}`;
	}

	searchProjects(query: string, page: number = 0, pageSize: number = 25) {
		return `${this.baseUrl}/searchProjects?query=${query}&page=${page}&pageSize=${pageSize}`;
	}

	getProjectNotesCount(): string {
		return `${this.baseUrl}/projectNotesCount`;
	}

	getCarriersCount(): string {
		return `${this.baseUrl}/carriersCount`;
	}

	updateCarrierWarmupSetting(): string {
		return `${this.baseUrl}/updateCarrierWarmupSetting`;
	}

	adminUpdateUser(): string {
		return `${this.baseUrl}/admin/account/user`;
	}

	listHotLanes(): string {
		return `${this.baseUrl}/hotLanes`;
	}

	notificationSettings(): string {
		return `${this.baseUrl}/notificationSettings`;
	}

	getLaneCountByAccountId(accountId: number): string {
		return `${this.baseUrl}/accountCarrierMaps/laneCount?accountId=${accountId}`;
	}

	getAccountsAndUsers(): string {
		return `${this.baseUrl}/accounts/users`;
	}

	getAccountReviewTransactions(accountId: number, page: number = 0, pageSize: number = 25): string {
		return `${this.baseUrl}/reviewTransactions/account/${accountId}?page=${page}&pageSize=${pageSize}`;
	}

	deleteAllAccountReviewTransactions(importStatId: string): string {
		return `${this.baseUrl}/reviewTransactions/account/${importStatId}`;
	}

	deleteAccountReviewTransaction(accountId: number): string {
		return `${this.baseUrl}/reviewTransactions/importStat/${accountId}`;
	}

	searchBrokers(query: string, page: number = 0, pageSize: number = 25) {
		return `${this.baseUrl}/searchBrokers?query=${query}&page=${page}&pageSize=${pageSize}`;
	}

	getBrokerById(accountId: string | number): string {
		return `${this.baseUrl}/accountDetails?accountId=${accountId}`;
	}

	checkProject(): string {
		return `${this.baseUrl}/checkProject`;
	}

	bulkLanePricing(): string {
		return `${this.baseUrl}/lanePricing/bulk`;
	}

	approveCarrierLane(): string {
		return `${this.baseUrl}/approveCarrierLane`;
	}

	bulkLoadsCount(): string {
		return `${this.baseUrl}/loads/count/bulk`;
	}

	getCarriersBidList(page: number = 0, pageSize: number = 25, accountId: number): string {
		return `${this.baseUrl}/carriers/${accountId}/bids?page=${page}&pageSize=${pageSize}`;
	}

	networkAccounts(page: number = 0, pageSize: number = 25): string {
		return `${this.baseUrl}/networkAccounts?page=${page}&pageSize=${pageSize}`;
	}

	importCarrierManagers(): string {
		return `${this.baseUrl}/api/v1/accountCarrierMaps/carrierManagers/import`;
	}

	getAccountCount(): string {
		return `${this.baseUrl}/accounts/count`;
	}

	getProjectsCount(): string {
		return `${this.baseUrl}/projects/-/count`;
	}

	getPayingUsersCount(): string {
		return `${this.baseUrl}/account/billableUsers`;
	}

	projectCarrierMapsByProjectIdsAndCarrierId(carrierId: string): string {
		return `${this.baseUrl}/projectCarrierMaps/projectIds?carrierId=${carrierId}`;
	}

	getLoadsByProjectId(projectId: string): string {
		return `${this.baseUrl}/freights/projects/${projectId}`;
	}

	getBidsByLoadIds(carrierId: string): string {
		return `${this.baseUrl}/bids/carriers/${carrierId}`;
	}

	getLoadByIds(): string {
		return `${this.baseUrl}/freight/loads`;
	}

	multipleBiddingNotification(): string {
		return `${this.baseUrl}/bids/notification`;
	}

	getBidsList(): string {
		return `${this.baseUrl}/bids/list`;
	}

	listLastLoads(): string {
		return `${this.baseUrl}/lastLoads`;
	}


	getCarrier(id: string, mc: any, dot: any, requestSource: any, minimal: boolean = false): string {
		return `${this.baseUrl}/carrier?id=${id ? id : ''}&mc=${mc ? mc : ''}&dot=${
			dot ? dot : ''
		}&source=${requestSource ? requestSource : ''}&minimal=${minimal}`;
	}

	downloadCarriers(search: any): string {
		return (
			`${this.baseUrl}/carriers/download?searchTerm=${search.searchTerm}&searchField=${search.searchField}&withoutLanes=${search.withoutLanes}` +
			`&inNetwork=${search.inNetwork}` +
			`&notOnboarded=${search.notOnboarded ? search.notOnboarded : false}&carrierRecruiter=${
				search.carrierRecruiter ? search.carrierRecruiter : false
			}` +
			`&accountIds=${search.accountIds ? search.accountIds : ''}&stageIds=${
				search.stageIds ? search.stageIds : ''
			}` +
			`&nextContactDate=${search.nextContactDate ? DATE_YMD(search.nextContactDate) : ''}`
		);
	}

	inboundEmailsList(page: number = 0, pageSize: number = 25): string {
		return `${this.baseUrl}/admin/inboundEmails?&page=${page}&pageSize=${pageSize}`;
	}

	inboundEmailFilters(filterTypes: any, startDate?: string, endDate?: string): string {
		let url = `${this.baseUrl}/admin/inboundEmailFilters?&filterType=${
			filterTypes ? filterTypes : ''
		}`;
		if (startDate) {
			url += `&startDate=${startDate}`;
		}
		if (endDate) {
			url += `&endDate=${endDate}`;
		}
		return url;
	}

	inboundEmailPart(emailId: string): string {
		return `${this.baseUrl}/admin/inboundEmails/${emailId}/part`;
	}

	getCarrierRecruiterLogs(): string {
		return `${this.baseUrl}/admin/listCarrierRecruiterLogs`;
	}

	createCarrierRecruiterLog(): string {
		return `${this.baseUrl}/admin/carrierRecruiterLogs`;
	}

	createCarrierRecruiterCalendarEvent(): string {
		return `${this.baseUrl}/admin/carrierRecruiter/calendarEvents`;
	}

	getAccountCarrierRecruiterMaps(accountId: number): string {
		return `${this.baseUrl}/admin/accounts/${accountId}/carrierRecruiters`;
	}

	createAccountCarrierRecruiterMaps(): string {
		return `${this.baseUrl}/admin/accountCarrierRecruiterMaps`;
	}

	deleteAccountCarrierRecruiterMaps(id: number): string {
		return `${this.baseUrl}/admin/accountCarrierRecruiterMap/${id}`;
	}

	getCarrierRecruiterReports(): string {
		return `${this.baseUrl}/admin/reports/carrierRecruiters`;
	}

	getAllCarrierRecruiters(): string {
		return `${this.baseUrl}/admin/carrierRecruiters`;
	}

	getRecruiterCarriersReport(): string {
		return `${this.baseUrl}/admin/reports/carrierRecruiters/carriers`;
	}

	getRecruiterLanesReport(
		userId: any,
		page: number = 0,
		pageSize: number = 25,
		startDate: string,
		endDate: string,
		carrierId: string,
		stageId: string
	): string {
		return (
			`${this.baseUrl}/admin/reports/carrierRecruiters/${userId}/lanes?page=${page}&pageSize=${pageSize}` +
			`&startDate=${startDate}&endDate=${endDate}` +
			`&carrierId=${carrierId || ''}&stageId=${stageId || ''}`
		);
	}

	getCalendarEvents(
		page: number = 0,
		pageSize: number = 25,
		carrierId: string,
		nextContactDate: string = null
	): string {
		return (
			`${this.baseUrl}/admin/carrierRecruiter/calendarEvents?page=${page}&pageSize=${pageSize}` +
			`&carrierId=${carrierId || ''}&nextContactDate=${nextContactDate ? nextContactDate : ''}`
		);
	}

	createCarrierRecruiterMapping(): string {
		return `${this.baseUrl}/admin/carrierRecruiterMappings`;
	}

	updateCarrierRecruiterMapping(): string {
		return `${this.baseUrl}/admin/carrierRecruiterMappings`;
	}

	listCarrierRecruiterMapping(): string {
		return `${this.baseUrl}/admin/listCarrierRecruiterMappings`;
	}

	updateOrDeleteCarrierRecruiterLog(id: string): string {
		return `${this.baseUrl}/admin/carrierRecruiterLogs/${id}`;
	}

	addUpdateDispatchUser(): string {
		return `${this.baseUrl}/addUpdateDispatchUser`;
	}

	getDispatchUser(accountId: Number): string {
		return `${this.baseUrl}/getDispatchUser/${accountId}`;
	}

	getAllAccountsForRecruiters(): string {
		return `${this.baseUrl}/admin/accountCarrierRecruiterMaps`;
	}

	getAggregationList(): string {
		return `${this.baseUrl}/admin/bidEngagementReport`;
	}

	searchAccountsUrl(carrierName: string): string {
		return `${this.baseUrl}/searchAccounts?q=${carrierName}`;
	}

	createAddOnProduct(): string {
		return `${this.baseUrl}/admin/addOnProducts`;
	}

	getAddOnProductsList(): string {
		return `${this.baseUrl}/admin/addOnProducts`;
	}

	updateAddOnProduct(id: number): string {
		return `${this.baseUrl}/admin/addOnProducts/${id}`;
	}

	getAddOnSubscriptions(accountId: number): string {
		return `${this.baseUrl}/admin/addOnSubscriptions/account/${accountId}`;
	}

	createAddOnSubscriptions(accountId: number, addOnProductId: number): string {
		return `${this.baseUrl}/admin/addOnSubscriptions/account/${accountId}/product/${addOnProductId}`;
	}

	cancelAddOnSubscriptions(subscriptionId: number): string {
		return `${this.baseUrl}/admin/addOnSubscriptions/${subscriptionId}/cancel`;
	}

	getCarriersByAccountId(accountId: number): string {
		return `${this.baseUrl}/admin/carriers/accountId/${accountId}`;
	}

	adminValidateCarrier(accountId: number): string {
		return `${this.baseUrl}/admin/account/${accountId}/validateCarrier`;
	}

	adminValidateCarrierById(carrierId: string): string {
		return `${this.baseUrl}/admin/carrier/${carrierId}/validate`;
	}

	adminMcpQualityData(mc: string, dot: string, carrierId: string, inNetwork = false): string {
		return `${this.baseUrl}/mcp/qualityData?mc=${mc || ''}&dot=${dot || ''}&carrierId=${carrierId}&inNetwork=${inNetwork}`;
	}

	getCustomersByAccountId(accountId: number): string {
		return `${this.baseUrl}/admin/customers/accountId/${accountId}`;
	}

	updateUserSettingUrl({ settingName, value }: { settingName: string; value: any }): string {
		return `${this.baseUrl}/userSetting?${'value=' + value + '&settingName=' + settingName}`;
	}

	saveEmailTemplate(): string {
		return `${this.baseUrl}/emailTemplates`;
	}

	getEmailTemplate(templateType: string): string {
		return `${this.baseUrl}/emailTemplates/getTemplate?templateType=${templateType}`;
	}

	deleteEmailTemplate(id: number): string {
		return `${this.baseUrl}/emailTemplates/${id}`;
	}

	sendTestEmail(): string {
		return `${this.baseUrl}/emailTemplates/testEmail`;
	}

	getEmailSignature(): string {
		return `${this.baseUrl}/emailTemplates/emailSignature`;
	}

	saveEmailSignature(): string {
		return `${this.baseUrl}/emailTemplates/emailSignature`;
	}

	deleteCustomEmailSignature(id: number): string {
		return `${this.baseUrl}/emailTemplates/emailSignature/${id}`;
	}

	createAccountEmailMap(): string {
		return `${this.baseUrl}/admin/accountEmailMaps`;
	}

	getAccountEmailMap(accountId: number, pageNumber: number, pageSize: number): string {
		return `${this.baseUrl}/admin/account/${accountId}/accountEmailMaps?page=${pageNumber}&pageSize=${pageSize}`;
	}

	updateAccountEmailMap(): string {
		return `${this.baseUrl}/admin/accountEmailMaps`;
	}

	deleteAccountEmailMap(accountEmailMapId: number): string {
		return `${this.baseUrl}/admin/accountEmailMaps/${accountEmailMapId}`;
	}

	getPricingFeedback(): string {
		return `${this.baseUrl}/admin/pricingFeedback`;
	}

	getAccountPlanProductMaps(
		accountId: number,
		pageNumber: number,
		pageSize: number,
		planProductId: string = null
	): string {
		return (
			`${this.baseUrl}/admin/accountPlanProductsMaps?accountId=${accountId}&page=${pageNumber}&pageSize=${pageSize}` +
			`${planProductId ? '&planProductId=' + planProductId : ''}`
		);
	}

	getDefaultTemplate(templateType: string): string {
		return `${this.baseUrl}/emailTemplates/defaultTemplates?templateType=${templateType}`;
	}

	updateProduct(productId: string): string {
		return `${this.baseUrl}/admin/products/${productId}`;
	}

	saveDefaultTemplate(): string {
		return `${this.baseUrl}/emailTemplates/defaultTemplates`;
	}

	restoreBackupTemplate(): string {
		return `${this.baseUrl}/emailTemplates/defaultTemplates`;
	}

	getCarriersMailReceiverContacts(): string {
		return `${this.baseUrl}/carriers/contacts`;
	}

	adminUpdateEmailSender(userId: number): string {
		return `${this.baseUrl}/admin/account/user/emailSender/${userId}`;
	}

	getPlanProductForAccount(accountId: number, productName: string): string {
		return `${this.baseUrl}/accounts/${accountId}/planProducts?productName=${productName}`;
	}

	addAccountCarrierContactMapsUrl(): string {
		return `${this.baseUrl}/accountCarrierContactMaps`;
	}

	getProjectCarrierMapsList(): string {
		return `${this.baseUrl}/projectCarrierMaps-list`;
	}

	onboardCarriers(accountId: number): string {
		return `${this.baseUrl}/admin/mcpIntegration/${accountId}`;
	}

	getAccountIntegrations(accountId: number): string {
		return `${this.baseUrl}/admin/integrations/${accountId}`;
	}

	getAccountAuthorizedApiClients(): string {
		return `${this.baseUrl}/admin/oauthClients/account`;
	}

	updateAddOnSubscription(): string {
		return `${this.baseUrl}/admin/addOnSubscriptions`;
	}

	updateSubscription(): string {
		return `${this.baseUrl}/admin/subscriptions`;
	}

	sendAnalyticsEvent(notificationClassName: string): string {
		return `${this.baseUrl}/analyticsEvent?className=${notificationClassName}`;
	}

	integrationTypesUrl(): string {
		return `${this.baseUrl}/integrationTypes`;
	}

	getBulkCapacityMatchJobsList(pageNumber: number, pageSize: number): string {
		return `${this.baseUrl}/bulkCapacityMatchJobs-list?page=${pageNumber}&pageSize=${pageSize}`;
	}

	getImportStatsUrl(): string {
		return `${this.baseUrl}/importStats/list`;
	}

	// CN-8862 (Bulk Rating): paged per-lane results for one price_lanes job. The account is
	// derived server-side from the session in c4, so no accountId is sent from the client.
	getImportStatResultsUrl(importStatId: string, page: number, pageSize: number): string {
		return `${this.baseUrl}/importStats/${importStatId}/results?page=${page}&pageSize=${pageSize}`;
	}

	// CN-9074 (Coverage / plan B6). No accountId is ever sent: c4 derives it from the session and
	// resolves the demo-account read redirect server-side, so the client cannot ask for an
	// account and therefore cannot ask for the wrong one.
	getCoverageReportUrl(
		runId: string | null,
		page: number,
		pageSize: number,
		includeSpot: boolean
	): string {
		const params = [`page=${page}`, `pageSize=${pageSize}`];

		if (runId) {
			params.push(`runId=${encodeURIComponent(runId)}`);
		}

		// Sent only when true. c4 defaults it to false, and putting the word "false" on the wire
		// is how a truthiness check somewhere inverts the toggle and shows a customer the spot
		// freight they filtered out.
		if (includeSpot) {
			params.push('includeSpot=true');
		}

		return `${this.baseUrl}/coverage/report?${params.join('&')}`;
	}

	getCoverageRunsUrl(limit?: number): string {
		return limit
			? `${this.baseUrl}/coverage/runs?limit=${limit}`
			: `${this.baseUrl}/coverage/runs`;
	}

	// The lane key is `ZIP3|STATE|ZIP3|STATE|FAMILY`; the pipes MUST be encoded or the query
	// string is invalid and the lane is silently missed.
	getCoverageLaneUrl(runId: string, laneKey: string, historyLimit?: number): string {
		const params = [
			`runId=${encodeURIComponent(runId)}`,
			`laneKey=${encodeURIComponent(laneKey)}`,
		];

		if (historyLimit) {
			params.push(`historyLimit=${historyLimit}`);
		}

		return `${this.baseUrl}/coverage/lane?${params.join('&')}`;
	}

	getProjectImportStats(pageNumber: number, pageSize: number): string {
		return `${this.baseUrl}/projectImportStats?page=${pageNumber}&pageSize=${pageSize}`;
	}

	getCarrierAutomationStats(): string {
		return `${this.baseUrl}/carrier/automation/stats`;
	}

	getEnabledIntegration(): string {
		return `${this.baseUrl}/enabledIntegration`;
	}

	getEnabledIntegrationFields(): string {
		return `${this.baseUrl}/enabledIntegrationFields`;
	}

	getBookingAutomationConfigurationData(): string {
		return `${this.baseUrl}/bookingAutomationConfiguration`;
	}

	updateBookingAutomationConfigurationData(): string {
		return `${this.baseUrl}/bookingAutomationConfiguration`;
	}

	// CN-8675: CargoChief-admin read (GET) / set (POST) of the per-account autoAcceptEntitled
	// entitlement, which lives in the BA-config blob. Admin-gated in the c4 controller.
	autoAcceptEntitledUrl(accountId: number | string): string {
		return `${this.baseUrl}/bookingAutomationConfiguration/autoAcceptEntitled/${accountId}`;
	}

	getReprocessImportStatsUrl(): string {
		return `${this.baseUrl}/importStats/reprocess`;
	}

	reprocessLoadFiles(): string {
		return `${this.baseUrl}/bulkMatchLoads/reprocess`;
	}

	saveEmailSubject(): string {
		return `${this.baseUrl}/customEmailSubjects`;
	}

	deleteEmailSubject(id: number): string {
		return `${this.baseUrl}/customEmailSubjects/${id}`;
	}

	getDefaultEmailSubject(templateType: string): string {
		return `${this.baseUrl}/customEmailSubjects/defaultTemplates?templateType=${templateType}`;
	}

	authenticateAmazonFreight(): string {
		return `${this.baseUrl}/integrations/amazonFreight`;
	}

	refreshLWAAccessTokenn(): string {
		return `${this.baseUrl}/integrations/amazonFreight-refresh`;
	}

	getUserSortsAndFilters(): string {
		return `${this.baseUrl}/userSortsAndFilters/list`;
	}

	saveUserSortsAndFilters(): string {
		return `${this.baseUrl}/userSortsAndFilters`;
	}

	deleteUserSortsAndFilters(id: number): string {
		return `${this.baseUrl}/userSortsAndFilters/${id}`;
	}

	postIntegrationOAuth(): string {
		return `${this.baseUrl}/integrations/oAuth`;
	}

	awsPreSignedUrl(
		bucketName: string,
		fileName: string,
		fileType: string,
		byFilename: boolean = false
	): string {
		return `${this.baseUrl}/awsPreSignedUrl?bucketName=${bucketName}&fileName=${fileName}&fileType=${fileType}&byFilename=${byFilename}`;
	}

	updateAccountLogo(): string {
		return `${this.baseUrl}/accountLogo`;
	}

	triggerTurvoCarrierDataPull() {
		return `${this.baseUrl}/integrations/turvo/pullCarriers`;
	}

	getLoadsSourcingUrl(page: number, pageSize: number): string {
		return `${this.baseUrl}/loadSourcing/loadsList?page=${page}&pageSize=${pageSize}`;
	}

	getBatchLoadsSourcingUrl(page: number, pageSize: number): string {
		return `${this.baseUrl}/loadSourcing/batchLoadsList?page=${page}&pageSize=${pageSize}`;
	}

	getTransactionsByAccountId(
		page: number = 0,
		pageSize: number = 25,
		sortField: string = 'updated',
		sortOrder = 'desc'
	): string {
		return `${this.baseUrl}/admin/historicalLoads?&page=${page}&pageSize=${pageSize}&sortField=${sortField}&sortOrder=${sortOrder}`;
	}

	getProjectGroups(): string {
		return `${this.baseUrl}/projectGroups/list`;
	}

	createProjectGroups(): string {
		return `${this.baseUrl}/projectGroups`;
	}

	updateProjectGroup(projectGroupId): string {
		return `${this.baseUrl}/projectGroups/${projectGroupId}`;
	}

	deleteProjectGroups(): string {
		return `${this.baseUrl}/projects/groups`;
	}

	getCustomerByCustomerCode(customerCode: string) {
		return `${this.baseUrl}/customers/customerCode/${customerCode}`;
	}

	getGreenScreensLanePricing(): string {
		return `${this.baseUrl}/lanePricing/greenScreens`;
	}

	getSonarLanePricing(): string {
		return `${this.baseUrl}/lanePricing/sonar`;
	}

	getRecentTransactionsCount(): string {
		return `${this.baseUrl}/recentTransactions/count`;
	}

	getProjectEquipment(): string {
		return `${this.baseUrl}/projects/equipment`;
	}

	procureCarriersFromScorecard(): string {
		return `${this.baseUrl}/scorecards/carriers/procure`;
	}

	rerunBookingAssistant(loadId: string): string {
		return `${this.baseUrl}/bookingAssistant/rerun/${loadId}`;
	}

	cancelLoad(loadId: string): string {
		return `${this.baseUrl}/load/${loadId}/cancel`;
	}

	pullRMISCarrierFiles(): string {
		return `${this.baseUrl}/rmis/pullCarrierFiles`;
	}

	inviteRMISCarrier(carrierId: string, brokerAccountId: number, brokerUserId: number) {
		return `${this.baseUrl}/rmis/carriers/${carrierId}/invite?brokerAccountId=${brokerAccountId}&brokerUserId=${brokerUserId}`;
	}

	getMcpCarrierQuality(
		mc: string = '',
		dot: string = '',
		inNetwork = false,
		carrierId: string = ''
	) {
		return `${this.baseUrl}/mcp/carrierQuality?mc=${mc}&dot=${dot}&inNetwork=${inNetwork}&carrierId=${carrierId}`;
	}

	getHighwayCarrierQuality(
		mc: string = '',
		dot: string = '',
		inNetwork = false,
		carrierId: string = ''
	) {
		return `${this.baseUrl}/highway/carrierQuality?mc=${mc}&dot=${dot}&inNetwork=${inNetwork}&carrierId=${carrierId}`;
	}

	getRmisCarrierQuality(
		mc: string = '',
		dot: string = '',
		inNetwork = false,
		carrierId: string = ''
	) {
		return `${this.baseUrl}/rmis/carrierQuality?mc=${mc}&dot=${dot}&inNetwork=${inNetwork}&carrierId=${carrierId}`;
	}

	updateProjectCarrierMapContact(projectCarrierMapId: string): string {
		return `${this.baseUrl}/projectCarrierMap/${projectCarrierMapId}/contact`;
	}

	searchContacts(
		carrierId: string,
		emailAddressQuery: string,
		skipPrimaryContact: boolean
	): string {
		return `${this.baseUrl}/carriers/${carrierId}/contacts?emailAddressQuery=${emailAddressQuery}&skipPrimaryContact=${skipPrimaryContact}`;
	}

	getActiveLoadsCount(): string {
		return `${this.baseUrl}/freight/loadsCount`;
	}

	sendProcuredCarriersEmail(): string {
		return `${this.baseUrl}/projectCarrierMaps/emailProcuredCarriers`;
	}

	sendAccountConfirmationEmail(userId: number): string {
		return `${this.baseUrl}/confirmationEmail?userId=${userId}`;
	}

	findExistingUser(mc: string, dot: string, email: string): string {
		return `${this.baseUrl}/findExistingUser?mc=${mc}&dot=${dot}&email=${email}`;
	}

	getRegistrationRequest(trackingId: string): string {
		return `${this.baseUrl}/carrierRegistrationRequest?trackingId=${trackingId}`;
	}

	postRegistrationRequestWithEmail(): string {
		return `${this.baseUrl}/carrierRegistrationRequest`;
	}

	validateCarrierRegistrationHashKey(hashKey: string): string {
		return `${this.baseUrl}/carrierRegistrationRequest/validate?hashKey=${hashKey}`;
	}

	confirmCarrierRegistrationHashKey(hashKey: string): string {
		return `${this.baseUrl}/carrierRegistrationRequest/confirm?hashKey=${hashKey}`;
	}

	resendConfirmationEmailToCarrierRegistrationRequest(trackingId: string): string {
		return `${this.baseUrl}/carrierRegistrationRequest/resendConfirmationEmail?trackingId=${trackingId}`;
	}

	requestAccess(): string {
		return `${this.baseUrl}/requestAccess`;
	}

	/**
	 * PATCH /bids/viewed — SendGrid email-open tracking (legacy).
	 * Increments viewCount on every call (even for already-VIEWED bids).
	 * Used when a carrier opens an invite email. Processes SendGrid event data
	 * (message ID, template, timestamp). Routes to BidViewedHandler lambda.
	 */
	markBidsAsViewed(): string {
		return `${this.baseUrl}/bids/viewed`;
	}

	/**
	 * PATCH /bids/mark-viewed — UI view tracking for load-sourcing available loads page.
	 * Only transitions eligible bids (NEW/INVITED → VIEWED). Fires Segment analytics
	 * and grouped WebSocket notifications. Routes to MarkBidsViewedHandler lambda.
	 * Use this endpoint when marking bids as viewed from the carrier UI (not email opens).
	 */
	markBidsAsViewedFromUI(): string {
		return `${this.baseUrl}/bids/mark-viewed`;
	}

	getLanesListForCarrier(pageNumber: number, pageSize: number): string {
		return `${this.baseUrl}/carrier-lanes/search?page=${pageNumber}&pageSize=${pageSize}`;
	}

	deleteCarrierLanes(): string {
		return `${this.baseUrl}/carrier-lanes/delete`;
	}

	getIpAddress(): string {
		return `${this.baseUrl}/ip`;
	}

	getCarrierAccountsNetworkList(pageNumber: number, pageSize: number): string {
		return `${this.baseUrl}/carrier-network/accounts?page=${pageNumber}&pageSize=${pageSize}`;
	}

	excludeCarrierLanes(): string {
		return `${this.baseUrl}/carrier-lanes/exclusions`;
	}

	addCarrierLaneBrokerAccountMap(): string {
		return `${this.baseUrl}/carrier-lanes/broker-account-map`;
	}

	deleteCarrierLaneBrokerAccountMap(): string {
		return `${this.baseUrl}/carrier-lanes/broker-account-map`;
	}

	updateLoadOwner(): string {
		return `${this.baseUrl}/bids/owner-update`;
	}

	carrierRequestedOnboarding(
		carrierId: string,
		brokerAccountId: number,
		brokerUserId: number,
		integrationType: string
	) {
		return `${this.baseUrl}/carriers/${carrierId}/onboarding-requested?brokerAccountId=${brokerAccountId}&brokerUserId=${brokerUserId}&integrationType=${integrationType}`;
	}

	declineOffers(): string {
		return `${this.baseUrl}/carriers/bids/decline`;
	}

	carrierNetworkAnalysis(dot: string, mc: string) {
		const params = new URLSearchParams();
		if (dot) params.append('dot', dot);
		if (mc) params.append('mc', mc);
		return `${this.baseUrl}/carriers/network-analysis?${params.toString()}`;
	}

	geocodeUrl(lat, lng): string {
		return `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${environment.GOOGLE_API_KEY}`;
	}

	carrierQualityDataForBroker(carrierId: string, brokerDot: string, brokerMc: string): string {
		const params = new URLSearchParams();
		if (brokerDot) params.append('brokerDot', brokerDot);
		if (brokerMc) params.append('brokerMc', brokerMc);
		return `${this.baseUrl}/carriers/${carrierId}/broker-quality-data?${params.toString()}`;
	}

	validateCarrierContact(): string {
		return `${this.baseUrl}/carriers/contacts/email/validate`;
	}

	// Allocation URLs
	getAllocationUrl(projectId: string, carrierId: string): string {
		return `${this.baseUrl}/allocation/project/${projectId}/carrier/${carrierId}`;
	}

	createAllocationUrl(): string {
		return `${this.baseUrl}/allocation`;
	}

	updateAllocationUrl(allocationId: string): string {
		return `${this.baseUrl}/allocation/${allocationId}`;
	}

	getAllocationStatusUrl(allocationId: string): string {
		return `${this.baseUrl}/allocation/${allocationId}/status`;
	}

	getAllocationsForProjectUrl(projectId: string): string {
		return `${this.baseUrl}/allocation/project/${projectId}`;
	}

	deleteAllocationUrl(allocationId: string): string {
		return `${this.baseUrl}/allocation/${allocationId}`;
	}

	getCarrierFromCarrierSource(dot: string): string {
		return `${this.baseUrl}/carrier-source/dot/${dot}`;
	}

	getAccountUsersV2(pageNumber: number, pageSize: number): string {
		return `${this.baseUrl}/account/users/search?page=${pageNumber}&pageSize=${pageSize}`;
	}

	removeCarrierContacts(): string {
		return `${this.baseUrl}/carriers/contacts/remove`;
	}

	getLoadsEquipments(page: number = 0, pageSize: number = 25): string {
		return `${this.baseUrl}/scorecards/loads/equipments?&page=${page}&pageSize=${pageSize}`;
	}

	searchBrokerAccounts(query: string): string {
		return `${this.baseUrl}/admin/searchBrokerAccounts?q=${encodeURIComponent(query)}`;
	}

	setMappedBroker(accountId: number): string {
		return `${this.baseUrl}/admin/accounts/${accountId}/mappedBroker`;
	}

	bidContactsUrl(bidId: string): string {
		return `${this.baseUrl}/bid/${bidId}/contacts`;
	}
}
